import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface UpdateMobileDeviceActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}

export const UpdateMobileDeviceActionSchema = z.object({
  type: z.literal('Update_Mobile_Devices'),
  daysBack: z
    .number()
    .nullable()
    .refine((value) => value !== null, {
      message: 'A number is required.',
    }),
  remoteAccountWipe: z.string(),
  block: z.string(),
  delete: z.string(),
  remoteWipe: z.string(),
});

export type UpdateMobileDeviceActionType = z.infer<
  typeof UpdateMobileDeviceActionSchema
>;

// Type guard to check if an action is UpdateMobileDeviceActionType
function isUpdateMobileDeviceAction(
  action: PolicyAction
): action is UpdateMobileDeviceActionType {
  return action.type === 'Update_Mobile_Devices';
}

export default function UpdateMobileDeviceAction({
  setValue,
  watch,
  control,
  errors,
}: UpdateMobileDeviceActionProps) {
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Update_Mobile_Devices');
  const actionAtIndex = actionData[idx];
  if (!isUpdateMobileDeviceAction(actionAtIndex)) {
    return null;
  }

  const currentAction: UpdateMobileDeviceActionType = actionAtIndex;
  const error: FieldErrors<UpdateMobileDeviceActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack>
        <Controller
          name={`actionData.${idx}.daysBack`}
          control={control}
          render={() => (
            <TextField
              inputProps={{
                id: 'daysBack',
                min: 0,
              }}
              InputLabelProps={{
                htmlFor: 'daysBack',
              }}
              type="number"
              label="Days since last log in:"
              value={currentAction.daysBack}
              onChange={(event) => {
                const value =
                  event.target.value === ''
                    ? null
                    : parseInt(event.target.value);
                setValue(`actionData.${idx}.daysBack`, value, {
                  shouldValidate: true,
                });
              }}
              error={!!error?.daysBack}
              helperText={error?.daysBack?.message || ' '}
              sx={{ width: 250 }}
              autoComplete="off"
            />
          )}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={currentAction.remoteWipe === 'true'}
              onChange={() => {
                setValue(
                  `actionData.${idx}.remoteWipe`,
                  currentAction.remoteWipe === 'true' ? 'false' : 'true'
                );
              }}
            />
          }
          sx={{ width: 'fit-content' }}
          label="Remote Wipe Devices"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={currentAction.remoteAccountWipe === 'true'}
              onChange={() =>
                setValue(
                  `actionData.${idx}.remoteAccountWipe`,
                  currentAction.remoteAccountWipe === 'true' ? 'false' : 'true'
                )
              }
            />
          }
          sx={{ width: 'fit-content' }}
          label="Remove Account Wipe Devices"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={currentAction.block === 'true'}
              onChange={() =>
                setValue(
                  `actionData.${idx}.block`,
                  currentAction.block === 'true' ? 'false' : 'true'
                )
              }
            />
          }
          sx={{ width: 'fit-content' }}
          label="Block Devices"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={currentAction.delete === 'true'}
              onChange={() =>
                setValue(
                  `actionData.${idx}.delete`,
                  currentAction.delete === 'true' ? 'false' : 'true'
                )
              }
            />
          }
          sx={{ width: 'fit-content' }}
          label="Delete Devices"
        />
      </Stack>
    </Stack>
  );
}
